import { createSlice, PayloadAction, Draft } from "@reduxjs/toolkit";
import { PurchaseOrderResponseDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderResponseDto";
import { PurchaseOrderSummaryDto } from "../../shared/dtos/purchaseOrder/PurchaseOrderSummaryDto";
import { SupplierSummaryDto } from "../../shared/dtos/supplier/SupplierSummaryDto";
import {
  getInitialCrudState,
  generateCrudReducers,
} from "../utils/crudFactory";

export interface POCartItem {
  inventoryItemId: string;
  name: string;
  category?: string;
  quantity: number;
  unitCost: number;
}

interface PurchaseOrderInitialState {
  cartItems: POCartItem[];
  selectedSupplier: SupplierSummaryDto | null;
  cartSubtotal: number;
}

const calculateSubtotal = (items: POCartItem[]): number =>
  items.reduce(
    (acc, item) =>
      acc + Number(item.unitCost || 0) * Number(item.quantity || 0),
    0,
  );

const loadInitialState = (): PurchaseOrderInitialState => {
  try {
    const items = JSON.parse(localStorage.getItem("po_items") || "[]");
    const supplier = JSON.parse(localStorage.getItem("po_supplier") || "null");
    return {
      cartItems: items as POCartItem[],
      selectedSupplier: supplier,
      cartSubtotal: calculateSubtotal(items),
    };
  } catch (e) {
    return { cartItems: [], selectedSupplier: null, cartSubtotal: 0 };
  }
};

const initialState = {
  ...getInitialCrudState<PurchaseOrderResponseDto, PurchaseOrderSummaryDto>(),
  ...loadInitialState(),

  loadingConfirm: false,
  loadingReceive: false,
  loadingPay: false,
  loadingPdf: false,
  loadingEmail: false,

  errorConfirm: null as string | null,
  errorReceive: null as string | null,
  errorPay: null as string | null,
  errorPdf: null as string | null,
  errorEmail: null as string | null,

  pdfUrl: null as string | null,
};

export const purchaseOrderSlice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    ...generateCrudReducers<
      PurchaseOrderResponseDto,
      PurchaseOrderSummaryDto
    >(),

    setLoadingConfirm(state) {
      state.loadingConfirm = true;
      state.errorConfirm = null;
    },
    setLoadingReceive(state) {
      state.loadingReceive = true;
      state.errorReceive = null;
    },
    setLoadingPay(state) {
      state.loadingPay = true;
      state.errorPay = null;
    },
    setLoadingPdf(state) {
      state.loadingPdf = true;
      state.errorPdf = null;
    },
    setLoadingEmail(state) {
      state.loadingEmail = true;
      state.errorEmail = null;
    },

    setErrorConfirm(state, action: PayloadAction<string | null>) {
      state.loadingConfirm = false;
      state.errorConfirm = action.payload;
    },
    setErrorReceive(state, action: PayloadAction<string | null>) {
      state.loadingReceive = false;
      state.errorReceive = action.payload;
    },
    setErrorPay(state, action: PayloadAction<string | null>) {
      state.loadingPay = false;
      state.errorPay = action.payload;
    },
    setErrorPdf(state, action: PayloadAction<string | null>) {
      state.loadingPdf = false;
      state.errorPdf = action.payload;
    },
    setErrorEmail(state, action: PayloadAction<string | null>) {
      state.loadingEmail = false;
      state.errorEmail = action.payload;
    },

    setPdfUrl(state, action: PayloadAction<string>) {
      state.loadingPdf = false;
      state.pdfUrl = action.payload;
    },
    clearPdfUrl(state) {
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      state.pdfUrl = null;
    },
    setEmailSuccess(state) {
      state.loadingEmail = false;
    },
    actionSuccess(state, action: PayloadAction<PurchaseOrderResponseDto>) {
      state.loadingConfirm = false;
      state.loadingReceive = false;
      state.loadingPay = false;
      state.item = action.payload as Draft<PurchaseOrderResponseDto>;
    },

    // --- Cart Reducers ---
    setPoSupplier(state, action: PayloadAction<SupplierSummaryDto | null>) {
      state.selectedSupplier =
        action.payload as Draft<SupplierSummaryDto> | null;
      localStorage.setItem("po_supplier", JSON.stringify(action.payload));
    },
    poItemAdd(state, action: PayloadAction<POCartItem>) {
      const existing = state.cartItems.find(
        (i) => i.inventoryItemId === action.payload.inventoryItemId,
      );
      if (existing) {
        existing.quantity += action.payload.quantity;
      } else {
        state.cartItems.push(action.payload as Draft<POCartItem>);
      }
      state.cartSubtotal = calculateSubtotal(state.cartItems as POCartItem[]);
      localStorage.setItem("po_items", JSON.stringify(state.cartItems));
    },
    poItemUpdateFields(
      state,
      action: PayloadAction<{
        inventoryItemId: string;
        updates: Partial<POCartItem>;
      }>,
    ) {
      const item = state.cartItems.find(
        (i) => i.inventoryItemId === action.payload.inventoryItemId,
      );
      if (item) {
        if (action.payload.updates.quantity !== undefined)
          item.quantity = Number(action.payload.updates.quantity);
        if (action.payload.updates.unitCost !== undefined)
          item.unitCost = Number(action.payload.updates.unitCost);
        if (item.quantity <= 0) {
          state.cartItems = state.cartItems.filter(
            (i) => i.inventoryItemId !== action.payload.inventoryItemId,
          );
        }
      }
      state.cartSubtotal = calculateSubtotal(state.cartItems as POCartItem[]);
      localStorage.setItem("po_items", JSON.stringify(state.cartItems));
    },
    poItemRemove(state, action: PayloadAction<string>) {
      state.cartItems = state.cartItems.filter(
        (i) => i.inventoryItemId !== action.payload,
      );
      state.cartSubtotal = calculateSubtotal(state.cartItems as POCartItem[]);
      localStorage.setItem("po_items", JSON.stringify(state.cartItems));
    },
    clearPoCart(state) {
      state.cartItems = [];
      state.cartSubtotal = 0;
      state.selectedSupplier = null;
      localStorage.removeItem("po_items");
      localStorage.removeItem("po_supplier");
    },
  },
});

export const purchaseOrderActions = purchaseOrderSlice.actions;
export default purchaseOrderSlice.reducer;
